/**
 * Runtime branding: one deployment serves many `mail.<domain>` hostnames
 * (ADR-0004), so the product name is derived from where it is served.
 */

/**
Brand identity for the current origin.
 */
export interface Brand {
  /**
  The bare domain this deployment fronts (e.g. `example.com`).
   */
  readonly domain: string;
  /**
  True when the host used the conventional `mail.` prefix.
   */
  readonly prefixed: boolean;
  /**
  Title-case product name (e.g. `Mail — Example.com`).
   */
  readonly title: string;
}

/**
Conventional subdomain that fronts the mail UI.
 */
const MAIL_PREFIX = "mail.";

/**
 * Derives brand identity from a hostname.
 *
 * @param hostname - The current `location.hostname`.
 * @returns The brand for that host: `mail.example.com` maps to
 * `Example.com`; anything else is used verbatim.
 */
export function brandFromHostname(hostname: string): Brand {
  const host = hostname.toLowerCase().replace(/\.$/u, "");
  const isPrefixed = host.startsWith(MAIL_PREFIX);
  const domain = isPrefixed ? host.slice(MAIL_PREFIX.length) : host;
  // Only the registrable label takes a capital; suffixes stay lower-case
  // ("Example.com", never "Example.Com").
  const label = domain.replace(/^./u, (first) => first.toUpperCase());
  return { domain, prefixed: isPrefixed, title: `Mail — ${label}` };
}
